import { useState, useCallback, useEffect, useRef } from 'react'
import { Session, SessionFilters, CommunicationStats, GroupedSession, ConciergeMode, Sentiment, Message } from '../types/communication.types'
import { useWebSocket } from './useWebSocket'
import CommunicationAPI from '../services/communicationApi'
import { AIAnalysisService } from '../services/aiAnalysisService'
import { 
  groupSessionsAggressively,
  groupedSessionToSession, 
  sortSessionsByPriority,
  debugSessions 
} from '../utils/sessionUtils'
import { messageEventBus } from '../utils/messageEventBus'

interface UseCommunicationCenterOptions {
  hotelId: number
  autoConnect?: boolean
}

interface UseCommunicationCenterReturn {
  // Estado das sessões (agrupadas para evitar duplicatas)
  sessions: Session[]
  filteredSessions: Session[]
  activeSessions: Session[]
  unreadSessions: Session[]
  
  // Estado da sessão ativa
  activeSession: Session | null
  setActiveSession: (session: Session | null) => void
  
  // Debug info para monitoramento de duplicatas
  debugInfo: {
    rawSessionsCount: number
    groupedSessionsCount: number
    duplicatesInfo: Array<{
      guestName: string
      sessionKey: string
      duplicateCount: number
      totalUnread: number
    }>
  }
  
  // Filtros
  filters: SessionFilters
  setFilters: (filters: Partial<SessionFilters>) => void
  updateFilter: (key: keyof SessionFilters, value: any) => void
  
  // Estados gerais
  isLoading: boolean
  error: string | null
  stats: CommunicationStats | null
  
  // WebSocket
  isConnected: boolean
  connectionError: string | null
  
  // 🤖 IA - Sugestões e análise (MÚLTIPLAS SUGESTÕES)
  aiSuggestions: Array<{
    id: string
    sessionKey: string
    suggestion: string
    sentiment: Sentiment
    originalPrompt: string
    timestamp: number
  }>
  isLoadingAISuggestion: boolean
  requestAISuggestion: (sessionKey: string, guestMessage: string) => Promise<void>
  acceptAISuggestion: (suggestionId: string, sessionKey: string, text: string) => void
  discardAISuggestion: (suggestionId: string) => void
  
  // Funções
  refreshSessions: () => void
  joinSession: (sessionKey: string) => void
  sendMessage: (sessionKey: string, text: string, imageUrl?: string) => void
  markSessionAsRead: (sessionKey: string) => void
  updateSessionConciergeMode: (sessionKey: string, mode: ConciergeMode) => void
  searchSessions: (query: string) => void
  loadStats: () => void
  
  // Socket functions
  reconnectSocket: () => void
  disconnectSocket: () => void
}

export const useCommunicationCenter = (
  options: UseCommunicationCenterOptions
): UseCommunicationCenterReturn => {
  const { hotelId, autoConnect = true } = options
  
  // Estados
  const [rawSessions, setRawSessions] = useState<Session[]>([]) // Sessões originais do backend
  const [originalSessions, setOriginalSessions] = useState<Session[]>([]) // 🔧 CÓPIA das sessões originais (para buscar IDs)
  const [groupedSessions, setGroupedSessions] = useState<GroupedSession[]>([]) // Sessões agrupadas
  const [activeSession, setActiveSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<CommunicationStats | null>(null)
  const [updateTrigger, setUpdateTrigger] = useState(0) // 🔥 Trigger para forçar atualizações
  
  // Buffer de mensagens recebidas (para quando a sessão não está aberta)
  const messageBufferRef = useRef<Map<string, any[]>>(new Map())
  
  // Estados para IA - MÚLTIPLAS SUGESTÕES
  const [aiSuggestions, setAiSuggestions] = useState<Array<{
    id: string // Identificador único da sugestão
    sessionKey: string
    suggestion: string
    sentiment: Sentiment
    originalPrompt: string
    timestamp: number
  }>>([])
  const [isLoadingAISuggestion, setIsLoadingAISuggestion] = useState(false)
  
  // Refs para evitar loops
  const isLoadingRef = useRef(false)
  
  // Filtros
  const [filters, setFiltersState] = useState<SessionFilters>({
    showUnreadOnly: false,
    showConnectedOnly: false,
    departments: [],
    searchQuery: ''
  })

  // Efeito para agrupar sessões sempre que as sessões cruas mudarem
  useEffect(() => {
    // Usar agrupamento mais agressivo para resolver problema das 44 sessões
    const grouped = groupSessionsAggressively(rawSessions)
    const sorted = grouped.sort(sortSessionsByPriority)
    setGroupedSessions(sorted)
    
    // Debug para identificar duplicatas
    debugSessions(rawSessions, grouped)
    
    console.log(' [SessionGrouping] Sessões reagrupadas:', sorted.length, 'trigger:', updateTrigger)
  }, [rawSessions, updateTrigger])

  // Efeito para buscar mensagens por departamento quando filtro é selecionado
  useEffect(() => {
    const fetchDepartmentMessages = async () => {
      // Se nenhum departamento selecionado ou múltiplos departamentos, restaurar todas as sessões
      if (filters.departments.length !== 1) {
        console.log('[DepartmentFilter] Restaurando todas as sessões (filtro desmarcado ou múltiplo)')
        
        // Recarregar todas as sessões normalmente chamando a API diretamente
        if (!isLoadingRef.current && hotelId) {
          isLoadingRef.current = true
          setIsLoading(true)
          
          try {
            const result = await CommunicationAPI.getFilteredSessions({ hotelId })
            
            const mappedSessions = result.sessions?.map((session: any) => ({
              ...session,
              department: session.channel || session.department || 'outro',
              conciergeMode: session.conciergeMode || 'manual'
            })) || []
            
            setRawSessions(mappedSessions)
            setOriginalSessions(mappedSessions) // 🔧 Salvar cópia para buscar IDs depois
          } catch (err) {
            console.error('Erro ao restaurar sessões:', err)
          } finally {
            setIsLoading(false)
            isLoadingRef.current = false
          }
        }
        return
      }

      const selectedDepartment = filters.departments[0]
      
      // CRITICAL FIX: Se originalSessions vazio, buscar TODAS as sessões primeiro
      if (originalSessions.length === 0) {
        console.log('[DepartmentFilter] originalSessions vazio, buscando sessões completas primeiro...')
        try {
          const result = await CommunicationAPI.getFilteredSessions({ hotelId })
          const mapped = result.sessions?.map((session: any) => ({
            ...session,
            department: session.channel || session.department || 'outro',
            conciergeMode: session.conciergeMode || 'manual'
          })) || []
          console.log(`[DepartmentFilter] ${mapped.length} sessões carregadas em originalSessions`)
          setOriginalSessions(mapped)
        } catch (err) {
          console.error(' [DepartmentFilter] Erro ao carregar sessões completas:', err)
        }
      }
      
      console.log(`[DepartmentFilter] Buscando todas as mensagens do departamento: ${selectedDepartment}`)

      try {
        setIsLoading(true)
        
        // Buscar todas as mensagens do departamento
        const response = await CommunicationAPI.getMessagesByChannel(
          hotelId,
          selectedDepartment,
          3, // últimos 3 dias
          100, // limite de 100 mensagens
          0 // offset 0
        )

        console.log(` [DepartmentFilter] Resposta da API:`, {
          totalMessages: response.messages?.length || 0,
          success: response.success
        })

        if (response.success && response.messages && response.messages.length > 0) {
          console.log(` [DepartmentFilter] PRIMEIRA MENSAGEM COMPLETA:`, JSON.stringify(response.messages[0], null, 2))
          
          // Agrupar mensagens por sessionKey
          const sessionMap = new Map<string, any>()

          response.messages.forEach((msg: any) => {
            const sessionKey = msg.session_sessionKey
            
            if (!sessionMap.has(sessionKey)) {
              console.log(` [DepartmentFilter] Mensagem completa da API:`, msg)
              
              // 🔧 FIX: API /by-channel não retorna session_id
              // Solução: buscar ID da sessão na lista ORIGINAL (antes do filtro)
              const existingSession = originalSessions.find((s: Session) => s.sessionKey === sessionKey)
              const existingSessionId = existingSession?.id
              const existingConciergeMode = existingSession?.conciergeMode || 'manual'
              const numericId = existingSessionId || msg.session_id || msg.message_sessionId || msg.sessionId || msg.id
              
              console.log(` [DepartmentFilter] Criando sessão:`, {
                numericId,
                existingSessionId,
                sessionKey,
                conciergeMode: existingConciergeMode,
                department: selectedDepartment || msg.message_channel || msg.session_department || 'recepcao',
                selectedDepartment,
                msg_channel: msg.message_channel,
                msg_session_department: msg.session_department,
                originalSessionsCount: originalSessions.length,
                resolucao: existingSessionId ? ' ID encontrado em sessão existente' : ' ID não disponível (será resolvido ao clicar)'
              })
              
              // Criar nova sessão
              sessionMap.set(sessionKey, {
                id: numericId, // Usa ID da sessão existente se disponível
                sessionKey: sessionKey,
                hotelId: hotelId,
                guestName: msg.session_guestName || 'Hóspede',
                room: msg.session_room || '',
                reservationId: '',
                // FIX: Priorizar selectedDepartment (filtro atual) sobre dados antigos do banco
                department: selectedDepartment || msg.message_channel || msg.session_department || 'recepcao',
                conciergeMode: existingConciergeMode, // Preservar conciergeMode da sessão original
                connected: false,
                unreadCount: 0,
                lastMessage: {
                  id: msg.message_id,
                  author: msg.message_author,
                  text: msg.message_text,
                  sentAt: msg.message_sentAt || msg.message_created,
                  channel: msg.message_channel
                },
                lastActivity: msg.message_created || msg.message_sentAt,
                messages: []
              })
            }

            // Adicionar mensagem à lista
            const session = sessionMap.get(sessionKey)
            session.messages.push({
              id: msg.message_id,
              author: msg.message_author,
              text: msg.message_text,
              sentAt: msg.message_sentAt || msg.message_created,
              channel: msg.message_channel
            })

            // Atualizar lastMessage se for mais recente
            const msgTime = new Date(msg.message_created || msg.message_sentAt).getTime()
            const lastMsgTime = new Date(session.lastActivity).getTime()
            
            if (msgTime > lastMsgTime) {
              session.lastMessage = {
                id: msg.message_id,
                author: msg.message_author,
                text: msg.message_text,
                sentAt: msg.message_sentAt || msg.message_created,
                channel: msg.message_channel
              }
              session.lastActivity = msg.message_created || msg.message_sentAt
            }
          })

          // Converter Map para Array e ordenar por última atividade
          const filteredSessions = Array.from(sessionMap.values()).sort((a, b) => {
            return new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
          })

          console.log(`[DepartmentFilter] Sessões filtradas criadas:`, {
            totalSessions: filteredSessions.length,
            sessions: filteredSessions.map(s => ({
              sessionKey: s.sessionKey,
              guestName: s.guestName,
              messagesCount: s.messages.length,
              lastMessage: s.lastMessage.text
            }))
          })

          // Atualizar estado com sessões filtradas
          setRawSessions(filteredSessions)
          setUpdateTrigger(prev => prev + 1)
        } else {
          // Nenhuma mensagem encontrada para este departamento
          console.log(`[DepartmentFilter] Nenhuma mensagem encontrada para ${selectedDepartment}`)
          setRawSessions([])
          setUpdateTrigger(prev => prev + 1)
        }
      } catch (err) {
        console.error(' [DepartmentFilter] Erro ao buscar mensagens por departamento:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDepartmentMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.departments, hotelId])

  // Callbacks para WebSocket
  const handleSessionsUpdate = useCallback((newSessions: Session[]) => {
    console.log('Atualizando sessões:', newSessions.length)
    
    // 🛡️ VALIDAÇÃO DUPLA: Filtrar sessões inválidas
    const validSessions = newSessions.filter(session => {
      const isValid = session.sessionKey && 
                     session.guestName && 
                     session.hotelId &&
                     session.id
      
      if (!isValid) {
        console.error('❌ [handleSessionsUpdate] Sessão INVÁLIDA ignorada:', {
          sessionKey: session.sessionKey,
          guestName: session.guestName,
          hotelId: session.hotelId,
          id: session.id
        })
      }
      
      return isValid
    })
    
    if (validSessions.length < newSessions.length) {
      console.warn(`⚠️ [handleSessionsUpdate] ${newSessions.length - validSessions.length} sessão(ões) inválida(s) filtrada(s)`)
    }
    
    console.log(`✅ [handleSessionsUpdate] ${validSessions.length} sessão(ões) válida(s) processada(s)`)
    
    setRawSessions(validSessions)
    // Atualizar também originalSessions quando não há filtro ativo
    if (filters.departments.length !== 1) {
      setOriginalSessions(validSessions)
    }
  }, [filters.departments])

  const handleNewMessage = useCallback((data: { 
    sessionKey: string; 
    message: any; 
    unreadCount: number; 
    guestName: string 
  }) => {
    console.log('[HANDLER NewMessage] CHAMADO com dados:', {
      sessionKey: data.sessionKey,
      messageText: data.message?.content || data.message?.text || data.message,
      messageChannel: data.message?.channel,
      unreadCount: data.unreadCount,
      guestName: data.guestName,
      timestamp: new Date().toISOString()
    })
    
    // ARMAZENAR MENSAGEM NO BUFFER (para quando o MessageView for aberto)
    const currentBuffer = messageBufferRef.current.get(data.sessionKey) || []
    currentBuffer.push(data.message)
    messageBufferRef.current.set(data.sessionKey, currentBuffer)
    console.log(`[MessageBuffer] Mensagem armazenada. Total no buffer: ${currentBuffer.length}`)
    
    // 1. Atualizar contadores não lidos nas sessões cruas
    setRawSessions(prev => {
      const updated = prev.map(session => 
        session.sessionKey === data.sessionKey
          ? { 
              ...session, 
              // INCREMENTAR unreadCount em vez de sobrescrever
              unreadCount: (session.unreadCount || 0) + 1,
              lastMessage: data.message,
              lastActivity: new Date().toISOString(),
              // MARCAR como ativa
              isActive: true,
              // ATUALIZAR DEPARTMENT com base no CHANNEL da mensagem
              department: data.message?.channel || session.department
            }
          : session
      )
      
      console.log('[NewMessage] Sessões atualizadas:', updated.length)
      
      // VERIFICAR se o contador foi realmente incrementado E se o department foi atualizado
      const targetSession = updated.find(s => s.sessionKey === data.sessionKey)
      console.log('[NewMessage] Sessão após atualização:', {
        sessionKey: targetSession?.sessionKey,
        unreadCount: targetSession?.unreadCount,
        department: targetSession?.department,
        messageChannel: data.message?.channel
      })
      console.log('🎯 [VALIDAÇÃO] Contador de não lidas incrementado:', {
        sessão: data.guestName,
        novoContador: targetSession?.unreadCount,
        mensagem: data.message?.text?.substring(0, 50)
      })
      
      return updated
    })

    // 2. FORÇAR ATUALIZAÇÃO DA UI
    setTimeout(() => {
      console.log('[NewMessage] Forçando atualização da UI...')
      setUpdateTrigger(prev => prev + 1)
    }, 100)

    // 3. PUBLICAR NO EVENT BUS PARA MENSAGENS EM TEMPO REAL
    console.log('[NewMessage] Publicando no event bus para useMessages...')
    messageEventBus.publish({
      sessionKey: data.sessionKey,
      message: data.message,
      guestName: data.guestName,
      unreadCount: data.unreadCount
    })

    // 4. Mostrar notificação
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`Nova mensagem de ${data.guestName}`, {
        body: data.message.text || 'Mensagem recebida',
        icon: '/favicon.ico'
      })
    }

    // 5. 🤖 MODO ASSISTIDO: Solicitar sugestão da IA automaticamente
    // Verificar se a sessão está em modo assistido
    const checkAssistedMode = async () => {
      const targetSession = rawSessions.find(s => s.sessionKey === data.sessionKey)
      
      if (targetSession?.conciergeMode === 'assisted' && data.message?.author === 'guest') {
        console.log('🤖 [MODO ASSISTIDO] Mensagem do hóspede detectada, solicitando sugestão da IA:', {
          sessionKey: data.sessionKey,
          guestName: data.guestName,
          messagePreview: data.message.text?.substring(0, 50)
        })
        
        // Disparar evento customizado para solicitar sugestão
        const event = new CustomEvent('request-ai-suggestion', {
          detail: {
            sessionKey: data.sessionKey,
            guestMessage: data.message.text || ''
          }
        })
        window.dispatchEvent(event)
      }
    }
    
    checkAssistedMode()
  }, [])

  const handleSessionConnected = useCallback((session: Session) => {
    console.log('Sessão conectada:', session.sessionKey)
    setRawSessions(prev => 
      prev.map(s => 
        s.sessionKey === session.sessionKey 
          ? { ...s, connected: true }
          : s
      )
    )
  }, [])

  const handleSessionDisconnected = useCallback((session: Session) => {
    console.log('Sessão desconectada:', session.sessionKey)
    setRawSessions(prev => 
      prev.map(s => 
        s.sessionKey === session.sessionKey 
          ? { ...s, connected: false }
          : s
      )
    )
  }, [])

  // 🔊 Função para tocar som de notificação (acessibilidade)
  const playNotificationSound = useCallback(() => {
    try {
      // Criar oscilador de áudio para tom de notificação suave
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // Tom agradável (nota C5 = 523.25 Hz)
      oscillator.frequency.value = 523.25
      oscillator.type = 'sine'
      
      // Volume suave
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.3)
      
      console.log('🔊 Som de notificação reproduzido')
    } catch (error) {
      console.warn('⚠️ Não foi possível reproduzir som de notificação:', error)
    }
  }, [])

  // 🤖 Handler para sugestões da IA vindas do backend via WebSocket
  const handleAISuggestion = useCallback((data: { sessionKey: string; suggestion: string; sentiment?: string }) => {
    console.log('🤖 [MODO ASSISTIDO] Sugestão da IA recebida via WebSocket:', {
      sessionKey: data.sessionKey,
      suggestionLength: data.suggestion?.length || 0,
      sentiment: data.sentiment,
      timestamp: new Date().toISOString()
    })
    
    try {
      const newSuggestion = {
        id: `${data.sessionKey}-${Date.now()}`, // ID único baseado em sessão + timestamp
        sessionKey: data.sessionKey,
        suggestion: data.suggestion,
        sentiment: (data.sentiment || 'neutral') as Sentiment,
        originalPrompt: '', // Não temos o prompt original quando vem do backend
        timestamp: Date.now()
      }
      
      // Adicionar nova sugestão ao array (mantém múltiplas sugestões)
      setAiSuggestions(prev => [...prev, newSuggestion])
      
      // 🔊 Notificação sonora para acessibilidade
      playNotificationSound()
      
      setIsLoadingAISuggestion(false)
      console.log('✅ [MODO ASSISTIDO] Nova sugestão adicionada. Total:', aiSuggestions.length + 1)
    } catch (error) {
      console.error('❌ [MODO ASSISTIDO] Erro ao processar sugestão da IA:', error)
      setIsLoadingAISuggestion(false)
    }
  }, [aiSuggestions.length, playNotificationSound])

  // Hook do WebSocket
  const {
    isConnected,
    connectionError,
    sendMessage: sendSocketMessage,
    joinSession: joinSocketSession,
    reconnect,
    disconnect
  } = useWebSocket({
    hotelId,
    onSessionsUpdate: handleSessionsUpdate,
    onNewMessage: handleNewMessage,
    onSessionConnected: handleSessionConnected,
    onSessionDisconnected: handleSessionDisconnected,
    onAISuggestion: handleAISuggestion,
    autoReconnect: autoConnect
  })

  // Função para atualizar filtros
  const setFilters = useCallback((newFilters: Partial<SessionFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }))
  }, [])

  const updateFilter = useCallback((key: keyof SessionFilters, value: any) => {
    setFiltersState(prev => ({ ...prev, [key]: value }))
  }, [])

  // Converter sessões agrupadas para o formato Session (para compatibilidade)
  const sessionsAsSession = groupedSessions.map(grouped => {
    const session = groupedSessionToSession(grouped)
    // Adicionar informações de debug como propriedade customizada
    ;(session as any)._debugInfo = {
      sessionCount: grouped.sessionCount,
      isDuplicated: grouped.sessionCount > 1
    }
    return session
  })

  // Função para filtrar sessões
  const applyFilters = useCallback((sessionsList: Session[]): Session[] => {
    return sessionsList.filter(session => {
      // Filtro por não lidas
      if (filters.showUnreadOnly && (!session.unreadCount || session.unreadCount === 0)) {
        return false
      }

      // Filtro por conectadas
      if (filters.showConnectedOnly && !session.connected) {
        return false
      }

      // Filtro por departamentos
      if (filters.departments.length > 0 && !filters.departments.includes(session.department)) {
        return false
      }

      // Filtro por busca
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase()
        const matchesName = session.guestName.toLowerCase().includes(query)
        const matchesRoom = session.room.toLowerCase().includes(query)
        const matchesReservation = session.reservationId.toLowerCase().includes(query)
        const matchesLastMessage = session.lastMessage?.text.toLowerCase().includes(query)
        
        if (!matchesName && !matchesRoom && !matchesReservation && !matchesLastMessage) {
          return false
        }
      }

      return true
    })
  }, [filters])

  // Sessões filtradas
  const filteredSessions = applyFilters(sessionsAsSession)

  // Sessões ativas (conectadas)
  const activeSessions = sessionsAsSession.filter(session => session.connected)

  // Sessões com mensagens não lidas
  const unreadSessions = sessionsAsSession.filter(session => session.unreadCount && session.unreadCount > 0)

  // Função para carregar sessões - CORRIGIDA SEM DEPENDÊNCIA DE FILTERS
  const refreshSessions = useCallback(async () => {
    if (!hotelId || isLoadingRef.current) return

    isLoadingRef.current = true
    setIsLoading(true)
    setError(null)

    try {
      console.log('Carregando sessões...')
      
      // Buscar todas as sessões (sem filtros na API para evitar loops)
      const result = await CommunicationAPI.getFilteredSessions({
        hotelId
      })

      // LOG: Verificar se as sessões do backend têm unreadCount
      console.log('🔍 [refreshSessions] Resposta do backend:', {
        totalSessions: result.sessions?.length || 0,
        sessionsWithUnread: result.sessions?.filter((s: any) => s.unreadCount && s.unreadCount > 0).length || 0
      })
      
      // LOG: Verificar se as sessões têm department, channel e conciergeMode
      if (result.sessions && result.sessions.length > 0) {
        const firstSession: any = result.sessions[0]
        console.log('[refreshSessions] Primeira sessão do backend:', {
          department: firstSession?.department,
          channel: firstSession?.channel,
          conciergeMode: firstSession?.conciergeMode,
          hasChannel: 'channel' in firstSession,
          hasDepartment: 'department' in firstSession,
          hasConciergeMode: 'conciergeMode' in firstSession
        })
        
        // 🔍 LOG ADICIONAL: Verificar TODAS as sessões com conciergeMode
        const sessionsWithMode = result.sessions.filter((s: any) => s.conciergeMode)
        console.log('🤖 [ConciergeMode] Sessões com conciergeMode definido:', {
          total: result.sessions.length,
          withMode: sessionsWithMode.length,
          modos: sessionsWithMode.map((s: any) => ({ 
            guest: s.guestName, 
            mode: s.conciergeMode 
          }))
        })
      }
      
      if (result.sessions && result.sessions.length > 0) {
        const withUnread = result.sessions.filter((s: any) => s.unreadCount && s.unreadCount > 0)
        if (withUnread.length > 0) {
          console.log('[refreshSessions] Sessões com unreadCount do backend:', 
            withUnread.map((s: any) => ({ name: s.guestName, count: s.unreadCount }))
          )
        }
      }

      // MAPEAR channel para department e preservar conciergeMode
      const mappedSessions = result.sessions?.map((session: any) => ({
        ...session,
        department: session.channel || session.department || 'outro',
        conciergeMode: session.conciergeMode || 'manual' // Preservar conciergeMode ou usar 'manual' como padrão
      })) || []

      setRawSessions(mappedSessions)
    } catch (err: any) {
      console.error('Erro ao carregar sessões:', err)
      setError(err.message || 'Erro ao carregar sessões')
    } finally {
      setIsLoading(false)
      isLoadingRef.current = false
    }
  }, [hotelId])

  // Função para entrar em uma sessão
  const joinSession = useCallback((sessionKey: string) => {
    const session = sessionsAsSession.find(s => s.sessionKey === sessionKey)
    if (session) {
      setActiveSession(session)
      joinSocketSession(sessionKey)
    }
  }, [sessionsAsSession, joinSocketSession])

  // Função para enviar mensagem
  const sendMessage = useCallback((sessionKey: string, text: string, imageUrl?: string) => {
    const session = sessionsAsSession.find(s => s.sessionKey === sessionKey)
    if (!session) return

    // USAR O CANAL DO FILTRO SELECIONADO
    // Se houver um departamento selecionado no filtro, usar ele
    // Se não houver filtro selecionado, usar 'recepcao' como padrão
    const channelToUse = filters.departments.length > 0 
      ? filters.departments[0] 
      : 'recepcao'

    console.log('[SendMessage] Enviando mensagem com canal:', channelToUse, '| Filtros:', filters.departments)

    sendSocketMessage({
      sessionKey,
      hotelId,
      author: 'agent',
      text,
      lang: 'pt',
      channel: channelToUse,
      imageUrl
    })
  }, [sessionsAsSession, hotelId, sendSocketMessage, filters.departments])

  // Função para marcar sessão como lida
  const markSessionAsRead = useCallback(async (sessionKey: string) => {
    try {
      console.log(` Marcando sessão como lida: ${sessionKey}`);
      
      await CommunicationAPI.markSessionAsRead(hotelId, sessionKey)
      
      // Atualizar estado local IMEDIATAMENTE
      setRawSessions(prev => {
        const updated = prev.map(session => 
          session.sessionKey === sessionKey 
            ? { ...session, unreadCount: 0 }
            : session
        )
        console.log(` Sessão ${sessionKey} marcada como lida localmente`);
        return updated;
      })
      
      // Forçar atualização da UI
      setUpdateTrigger(prev => prev + 1);
      
    } catch (err: any) {
      console.error(' Erro ao marcar como lida:', err)
    }
  }, [hotelId])

  /**
   * Atualizar conciergeMode de uma sessão localmente
   * 🔧 IMPORTANTE: Atualiza TODAS as sessões duplicadas do mesmo hóspede
   */
  const updateSessionConciergeMode = useCallback((sessionKey: string, mode: ConciergeMode) => {
    console.log(`[updateSessionConciergeMode] Atualizando: ${sessionKey} -> ${mode}`);
    
    // Primeiro, encontrar informações do hóspede para atualizar TODAS as suas sessões
    let targetGuest: string | null = null;
    let targetRoom: string | null = null;
    
    setRawSessions(prev => {
      // Encontrar a sessão alvo para pegar guestName e room
      const targetSession = prev.find(s => s.sessionKey === sessionKey);
      if (targetSession) {
        targetGuest = targetSession.guestName;
        targetRoom = targetSession.room;
        console.log(` [updateSessionConciergeMode] Hóspede identificado: ${targetGuest} - Quarto: ${targetRoom}`);
      }
      
      // Atualizar TODAS as sessões do mesmo hóspede (mesmo nome + mesmo quarto)
      const updated = prev.map(session => {
        const isSameGuest = session.guestName === targetGuest && session.room === targetRoom;
        if (isSameGuest) {
          console.log(` [updateSessionConciergeMode] Atualizando sessão duplicada: ${session.sessionKey} -> ${mode}`);
          return { ...session, conciergeMode: mode };
        }
        return session;
      })
      
      // Verificar quantas foram atualizadas
      const updatedCount = updated.filter(s => s.guestName === targetGuest && s.room === targetRoom).length;
      console.log(` [updateSessionConciergeMode] ${updatedCount} sessão(ões) atualizada(s) para: ${mode}`);
      
      return updated;
    })
    
    // Atualizar originalSessions também (todas as sessões do mesmo hóspede)
    setOriginalSessions(prev => 
      prev.map(session => {
        const isSameGuest = session.guestName === targetGuest && session.room === targetRoom;
        return isSameGuest ? { ...session, conciergeMode: mode } : session;
      })
    )
    
    // Forçar atualização da UI
    setUpdateTrigger(prev => {
      const newValue = prev + 1;
      console.log(` [updateSessionConciergeMode] Trigger atualizado: ${prev} -> ${newValue}`);
      return newValue;
    });
  }, [])

  // LISTENER PARA WEBSOCKET GLOBAL - Escutar eventos do WebSocketManager
  useEffect(() => {
    const handleGlobalWebSocketMessage = (event: CustomEvent) => {
      const { event: eventName, data } = event.detail;
      
      console.log('[GlobalWebSocket] Evento recebido:', eventName, data);
      
      // Tratar eventos de mensagem
      const messageEvents = [
        'guest-new-message',
        'force-refresh-messages', 
        'multi-session-message',
        'bidirectional-message',
        'webapp-message',
        'guest-message',
        'new-message',
        'message',
        'session-message',
        'hotel-message'
      ];
      
      if (messageEvents.includes(eventName)) {
        console.log('[GlobalWebSocket] Nova mensagem detectada:', data);
        
        // REMOVIDO: refreshSessions() aqui causava sobrescrita do unreadCount
        // O handleNewMessage já incrementa localmente, não precisamos buscar do backend
        // refreshSessions();
        
        // Se temos dados de mensagem estruturados, processar diretamente
        if (data && data.sessionKey && data.message) {
          handleNewMessage({
            sessionKey: data.sessionKey,
            message: data.message,
            unreadCount: data.unreadCount || 1,
            guestName: data.guestName || data.guest || 'Hóspede'
          });
        }
      }
    };

    // Adicionar listener para eventos do WebSocket global
    window.addEventListener('websocket-message', handleGlobalWebSocketMessage as EventListener);
    
    // LISTENER PARA REFRESH DE SESSÃO ESPECÍFICA
    const handleSessionRefresh = (event: any) => {
      const { sessionKey, triggerSource } = event.detail || {};
      console.log(' [SessionRefresh] Evento de refresh recebido:', { sessionKey, triggerSource });
      
      // REMOVIDO: refreshSessions() aqui causava sobrescrita do unreadCount
      // O evento de refresh vem DEPOIS da nova mensagem e apaga o contador local
      // refreshSessions();
      
      // Disparar trigger para garantir atualização da UI (sem buscar backend)
      setUpdateTrigger(prev => prev + 1);
    };
    
    window.addEventListener('websocket-session-refresh', handleSessionRefresh as EventListener);
    
    console.log(' [GlobalWebSocket] Listener adicionado para eventos do WebSocket global');

    // Cleanup
    return () => {
      window.removeEventListener('websocket-message', handleGlobalWebSocketMessage as EventListener);
      window.removeEventListener('websocket-session-refresh', handleSessionRefresh as EventListener);
    };
  }, [handleNewMessage, refreshSessions])

  //  ==================== FUNÇÕES DE IA ====================
  
  /**
   * Solicitar sugestão de resposta da IA (Modo Assistido)
   * Só será chamada quando conciergeMode for 'assisted'
   */
  const requestAISuggestion = useCallback(async (sessionKey: string, guestMessage: string) => {
    const session = sessionsAsSession.find(s => s.sessionKey === sessionKey)
    if (!session) {
      console.warn('[AI] Sessão não encontrada:', sessionKey)
      return
    }

    // Só chamar IA se modo for 'assisted'
    if (session.conciergeMode !== 'assisted') {
      console.log('[AI] Modo não é assistido, ignorando sugestão. Modo atual:', session.conciergeMode)
      return
    }

    setIsLoadingAISuggestion(true)

    try {
      console.log('[AI] Solicitando sugestão para:', {
        sessionKey,
        messagePreview: guestMessage.substring(0, 50) + '...',
        hotelId
      })

      const response = await AIAnalysisService.analyzeConcierge(
        guestMessage,
        sessionKey,
        hotelId
      )

      if (response.success) {
        const newSuggestion = {
          id: `${sessionKey}-${Date.now()}`,
          sessionKey,
          suggestion: response.suggestion,
          sentiment: response.sentiment,
          originalPrompt: guestMessage,
          timestamp: Date.now()
        }
        
        setAiSuggestions(prev => [...prev, newSuggestion])
        playNotificationSound()

        console.log('[AI] Sugestão recebida:', {
          sentiment: response.sentiment,
          confidence: response.confidence,
          suggestionLength: response.suggestion.length
        })
      } else {
        console.error('[AI] Falha ao gerar sugestão:', response)
      }
    } catch (error) {
      console.error('[AI] Erro ao solicitar sugestão:', error)
    } finally {
      setIsLoadingAISuggestion(false)
    }
  }, [sessionsAsSession, hotelId, playNotificationSound])

  /**
   * Aceitar sugestão da IA e enviar ao hóspede
   * ✨ IMPORTANTE: Também registra a mensagem no histórico do Admin
   */
  const acceptAISuggestion = useCallback((suggestionId: string, sessionKey: string, text: string) => {
    console.log('[AI] Aceitando sugestão e enviando:', {
      suggestionId,
      sessionKey,
      textLength: text.length,
      textPreview: text.substring(0, 50) + '...'
    })
    
    // 1️⃣ Enviar mensagem ao hóspede
    sendMessage(sessionKey, text)
    
    // 2️⃣ Adicionar mensagem ao histórico local do Admin IMEDIATAMENTE
    const session = sessionsAsSession.find(s => s.sessionKey === sessionKey)
    if (session) {
      const newMessage: Message = {
        id: Date.now(), // ID temporário até receber confirmação do backend
        sessionId: session.id || 0,
        author: 'agent',
        text: text, // ✅ Usar o texto EDITADO, não o original
        lang: 'pt',
        created: new Date(),
        deliveryStatus: 'sent',
        channel: session.department || 'recepcao',
        metadata: {
          isAISuggestion: true
        }
      }
      
      // Disparar evento para atualizar MessageView
      window.dispatchEvent(new CustomEvent('new-message-sent', {
        detail: { sessionKey, message: newMessage }
      }))
      
      console.log('✅ [AI] Mensagem registrada localmente no histórico:', {
        sessionKey,
        messageId: newMessage.id,
        text: text.substring(0, 50)
      })
    }
    
    // 3️⃣ Remover apenas a sugestão aceita
    setAiSuggestions(prev => prev.filter(s => s.id !== suggestionId))
  }, [sendMessage, sessionsAsSession])

  /**
   * Descartar sugestão da IA sem enviar
   */
  const discardAISuggestion = useCallback((suggestionId: string) => {
    console.log('[AI] Descartando sugestão:', suggestionId)
    setAiSuggestions(prev => prev.filter(s => s.id !== suggestionId))
  }, [])

  // ==================== FIM FUNÇÕES DE IA ====================

  // Função de busca
  const searchSessions = useCallback((query: string) => {
    updateFilter('searchQuery', query)
  }, [updateFilter])

  // Função para carregar estatísticas - ESTÁVEL
  const loadStats = useCallback(async () => {
    if (!hotelId) return

    try {
      console.log('Carregando estatísticas...')
      const statsData = await CommunicationAPI.getStats(hotelId)
      setStats(statsData)
    } catch (err: any) {
      console.error('Erro ao carregar estatísticas:', err)
    }
  }, [hotelId])

  // Solicitar permissão para notificações
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Carregar dados iniciais - USANDO REFS PARA EVITAR DEPENDÊNCIAS CIRCULARES  
  useEffect(() => {
    if (hotelId && autoConnect) {
      // Usar refs para evitar dependências circulares
      const timer = setTimeout(() => {
        // Chamar diretamente sem depender dos callbacks
        if (isLoadingRef.current) return
        
        isLoadingRef.current = true
        setIsLoading(true)
        setError(null)

        // CARREGAR SESSÕES REAIS DA API (SEM MOCK)  
        CommunicationAPI.getFilteredSessions({ hotelId })
          .then(result => {
            console.log(` Sessões reais carregadas: ${result.sessions?.length || 0}`)
            
            //  DEBUG: Inspecionar estrutura das sessões vindas do backend
            if (result.sessions && result.sessions.length > 0) {
              console.log('ESTRUTURA DA PRIMEIRA SESSÃO DO BACKEND:')
              const firstSession = result.sessions[0]
              console.log('- ID:', firstSession.id, '(tipo:', typeof firstSession.id, ')')
              console.log('- SessionKey:', firstSession.sessionKey)
              console.log('- Hotel ID:', firstSession.hotelId)
              console.log('- Guest Name:', firstSession.guestName)
              console.log('- Sessão completa:', firstSession)
            }
            
            setRawSessions(result.sessions || [])
            
            if (result.sessions?.length === 0) {
              console.log(' Nenhuma sessão encontrada - verificar se WebApp enviou mensagens')
            }
          })
          .catch(err => {
            console.error(' Erro ao carregar sessões reais:', err)
            setError(`Erro ao conectar com a API: ${err}`)
            setRawSessions([]) // Lista vazia em caso de erro
          })
          .finally(() => {
            setIsLoading(false)
            isLoadingRef.current = false
          })

        // Carregar estatísticas (com fallback)
        CommunicationAPI.getStats(hotelId)
          .then(statsData => {
            setStats(statsData)
          })
          .catch(err => {
            console.error('Erro ao carregar estatísticas:', err)
            // Não usar dados mock - deixar stats como null se houver erro
            setStats(null)
          })
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [hotelId, autoConnect])

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      // Cleanup sem dependências para evitar loops
      disconnect()
    }
  }, [disconnect])

  // 🤖 Listener para evento customizado de sugestão IA (modo assistido)
  useEffect(() => {
    const handleRequestAISuggestion = (event: Event) => {
      const customEvent = event as CustomEvent<{
        sessionKey: string
        guestMessage: string
      }>
      
      const { sessionKey, guestMessage } = customEvent.detail
      
      console.log('🤖 [EVENT LISTENER] Evento request-ai-suggestion recebido:', {
        sessionKey,
        messagePreview: guestMessage.substring(0, 50)
      })
      
      requestAISuggestion(sessionKey, guestMessage)
        .catch(err => console.error('❌ Erro ao processar sugestão IA:', err))
    }
    
    window.addEventListener('request-ai-suggestion', handleRequestAISuggestion)
    
    return () => {
      window.removeEventListener('request-ai-suggestion', handleRequestAISuggestion)
    }
  }, [requestAISuggestion])

  // Preparar informações de debug
  const duplicatesInfo = groupedSessions
    .filter(g => g.sessionCount > 1)
    .map(g => ({
      guestName: g.guestName,
      sessionKey: g.sessionKey,
      duplicateCount: g.sessionCount,
      totalUnread: g.totalUnreadCount
    }))

  return {
    // Estado das sessões (já agrupadas e ordenadas)
    sessions: filteredSessions,
    filteredSessions: filteredSessions,
    activeSessions,
    unreadSessions,
    
    // Estado da sessão ativa
    activeSession,
    setActiveSession,
    
    // Filtros
    filters,
    setFilters,
    updateFilter,
    
    // Estados gerais
    isLoading,
    error,
    stats,
    
    // WebSocket
    isConnected,
    connectionError,
    
    // IA - Sugestões e análise (MÚLTIPLAS)
    aiSuggestions,
    isLoadingAISuggestion,
    requestAISuggestion,
    acceptAISuggestion,
    discardAISuggestion,
    
    // Debug info
    debugInfo: {
      rawSessionsCount: rawSessions.length,
      groupedSessionsCount: groupedSessions.length,
      duplicatesInfo
    },
    
    // Funções
    refreshSessions,
    joinSession,
    sendMessage,
    markSessionAsRead,
    updateSessionConciergeMode,
    searchSessions,
    loadStats,
    
    // Socket functions
    reconnectSocket: reconnect,
    disconnectSocket: disconnect
  }
}